import { useEffect, useRef, useState } from 'react'
import type { TouchEvent } from 'react'
import {Activity, BarChart3} from 'lucide-react'

interface ProductCardProps {
    logoSrc: string;
    logoAlt: string;
    tagline: string;
    // Product screenshot for the media zone. When absent the zone stays
    // empty grey and shows mediaLabel (e.g. "Video — coming soon").
    mediaKind?: 'monitoring' | 'reporting';
    mediaSrc?: string;
    mediaAlt?: string;
    mediaLabel?: string;
    videoSrc?: string;
    animatedPreviewSrc?: string;
    posterSrc?: string;
    previewEnabled?: boolean;
    onOpen: () => void;
}

// A tap must not open the modal while scrolling the page. Recorded from
// touchstart to touchend: under this distance and duration, it's a tap;
// beyond either, it's a swipe and the click is suppressed.
const SWIPE_DISTANCE_THRESHOLD_PX = 10
const SWIPE_DURATION_THRESHOLD_MS = 300

// The whole card is the single control that opens the product modal — the
// "+" below is a visual affordance only (aria-hidden, not a button), so
// keyboard users get one tab stop per card, not two doing the same thing.
//
// Layout reference: src/assets/illustrations/product card.svg — outer frame,
// media/caption divider, and "+" utility button position and proportions.
export function ProductCard({ logoSrc, logoAlt, tagline, mediaKind, mediaSrc, mediaAlt, mediaLabel, videoSrc, animatedPreviewSrc, posterSrc, previewEnabled = true, onOpen }: ProductCardProps) {
    const [previewing, setPreviewing] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)
    const suppressClickRef = useRef(false)

    const stopPreview = () => {
        setPreviewing(false)
        const video = videoRef.current
        if (!video) return
        video.pause()
        video.currentTime = 0
    }

    const startPreview = () => {
        if (!previewEnabled || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
        setPreviewing(true)
        // Browser autoplay policy or a missing asset must never block the card.
        void videoRef.current?.play().catch(() => {})
    }

    useEffect(() => {
        if (!previewEnabled && videoRef.current) {
            videoRef.current.pause()
            videoRef.current.currentTime = 0
        }
    }, [previewEnabled])

    const handleTouchStart = (event: TouchEvent<HTMLButtonElement>) => {
        const touch = event.touches[0]
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() }
        suppressClickRef.current = false
    }

    const handleTouchEnd = (event: TouchEvent<HTMLButtonElement>) => {
        const start = touchStartRef.current
        if (!start) return

        const touch = event.changedTouches[0]
        const distance = Math.hypot(touch.clientX - start.x, touch.clientY - start.y)
        const duration = Date.now() - start.time

        if (distance >= SWIPE_DISTANCE_THRESHOLD_PX || duration >= SWIPE_DURATION_THRESHOLD_MS) {
            suppressClickRef.current = true
        }
    }

    const handleClick = () => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
        }
        stopPreview()
        onOpen()
    }

    return (
        <button
            type="button"
            data-analytics-action={'product_details_' + logoAlt.replace(/^unimo/i, '').toLowerCase()}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={startPreview}
            onMouseLeave={stopPreview}
            onFocus={startPreview}
            onBlur={stopPreview}
            aria-label={`View ${logoAlt} details`}
            className="flex h-full w-full flex-col overflow-hidden rounded-site-card border border-unimo-site-grey-300 bg-white text-left transition-colors hover:border-unimo-site-pink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-unimo-site-pink"
        >
            <div className="relative aspect-video w-full bg-unimo-site-grey-100">
                {animatedPreviewSrc && posterSrc ? (
                    <img
                        src={previewing && previewEnabled ? animatedPreviewSrc : posterSrc}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-contain"
                    />
                ) : videoSrc ? (
                    <video
                        ref={videoRef}
                        src={videoSrc}
                        poster={posterSrc}
                        muted
                        loop
                        playsInline
                        preload="none"
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : mediaSrc ? (
                    <img src={mediaSrc} alt={mediaAlt ?? ''} className="h-full w-full object-contain p-5" />
                ) : mediaKind ? (
                    <div className="flex h-full flex-col items-center justify-center gap-4 bg-unimo-stone text-unimo-slate">
                        {mediaKind==='monitoring'?<Activity size={64} strokeWidth={1} aria-hidden="true"/>:<BarChart3 size={64} strokeWidth={1} aria-hidden="true"/>}
                        <span className="text-sm font-light">{mediaKind==='monitoring'?'Live site insights':'Reports made clear'}</span>
                    </div>
                ) : null}
                {!videoSrc && !(animatedPreviewSrc && posterSrc) && !mediaSrc && !mediaKind && mediaLabel && (
                    <span className="absolute right-4 top-4 rounded bg-white/90 px-2 py-1 text-xs font-medium text-unimo-site-grey-700">
                        {mediaLabel}
                    </span>
                )}
            </div>

            <div className="flex w-full flex-1 flex-col gap-4 border-t border-unimo-site-grey-300 p-4 lg:p-6">
                <div className="flex items-center justify-between gap-4">
                    <img src={logoSrc} alt={logoAlt} className="h-auto max-h-6 min-w-0 max-w-[calc(100%-34px)]" />
                    <span
                        aria-hidden="true"
                        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm bg-unimo-site-pink text-unimo-stone"
                    >
                        <svg viewBox="0 0 20 20" width="8" height="8" fill="none">
                            <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                    </span>
                </div>
                {/* min-height, not height: reserves space for the longest wrap (3
                lines at mobile card widths, 2 at sm/lg) without ever clipping
                copy. Combined with h-full above, every card in the row stretches
                to match whichever card's tagline actually needs the most lines. */}
                <p className="min-h-[72px] text-base leading-6 font-medium text-unimo-slate sm:min-h-12">
                    {tagline}
                </p>
            </div>
        </button>
    )
}

export default ProductCard
